/* global GoogleMapsLoader*/

import React, { PropTypes } from "react";
import ComponentRegistry from "../../Common/ComponentRegistry"
import MarkerClusterer from "marker-clusterer-plus-es2015";
import extend from "extend";

import GoogleMarkerStore from "../Stores/MarkerStore";

function extractAddress(results) {
    const addresObj = { postal_code: null, street_number: null, route: null, country_code: null, city: null, lat: null, lng: null };

    Object.values(results[0].address_components).forEach(function(address) {
        switch (true) {
            case address.types.indexOf('street_number') >= 0:
                addresObj['street_number'] = address.long_name;
                break;
            case address.types.indexOf('route') >= 0:
                addresObj['route'] = address.long_name;
                break;
            case address.types.indexOf('locality') >= 0:
                addresObj['city'] = address.short_name;
                break;
            case address.types.indexOf('postal_code') >= 0:
                addresObj['postal_code'] = address.long_name;
                break;
            case address.types.indexOf('country') >= 0:
                addresObj['country_code'] = address.short_name;
                break;
        }
    });

    addresObj['lat'] = results[0].geometry.location.lat;
    addresObj['lng'] = results[0].geometry.location.lng;

    return addresObj;
}

export default class GoogleMapController extends React.Component {
    constructor(props) {
        super(props);
        this.store = new GoogleMarkerStore();
        this.panned = true;
    }

    componentDidMount() {
        if (typeof GoogleMapsLoader !== "undefined") {
            GoogleMapsLoader.KEY = this.props.apiKey;
            GoogleMapsLoader.LIBRARIES = this.props.libraries;
            GoogleMapsLoader.LANGUAGE = this.props.language;
            GoogleMapsLoader.REGION = this.props.region;
            GoogleMapsLoader.load((google) => {
                this.google = google;
                this.geocoder = new google.maps.Geocoder();

                this.map = new google.maps.Map(this.mapNode, extend({}, this.props.options, {
                    center: new google.maps.LatLng(
                        this.props.options.center.latitude,
                        this.props.options.center.longitude,
                    ),
                }));

                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition((position) => {
                        this.map.setCenter(new google.maps.LatLng(position.coords.latitude, position.coords.longitude));
                    });
                }

                this.defaultIcon = {
                    url: this.props.defaultIcon.url,
                    size: new google.maps.Size(this.props.defaultIcon.height, this.props.defaultIcon.width),
                    // The origin for this image is 0,0
                    origin: new google.maps.Point(0, 0),
                    // The anchor for this image is half the width of the icon
                    anchor: new google.maps.Point(0, this.props.defaultIcon.width / 2),
                };
                this.activeIcon = {
                    url: this.props.activeIcon.url,
                    size: new google.maps.Size(this.props.activeIcon.height, this.props.activeIcon.width),
                    // The origin for this image is 0,0
                    origin: new google.maps.Point(0, 0),
                    // The anchor for this image is half the width of the icon
                    anchor: new google.maps.Point(0, this.props.activeIcon.width / 2),
                };

                if (this.props.clusterOptions !== false) {
                    this.cluster = new MarkerClusterer(this.map, this.store.markers, this.props.clusterOptions);
                }

                this.map.addListener("center_changed", () => {
                    this.panned = true;
                });

                this.map.addListener("idle", () => {
                    if (this.panned) {
                        this.panned = false;
                        this.geocoder.geocode({ latLng: this.map.getCenter() }, (results, status) => {
                            if (status === this.google.maps.GeocoderStatus.OK) {
                                this.onLocationChange(extractAddress(results), results[0].geometry.location);
                            }
                        });
                    }
                });

                let address = this.props.address;
                if (typeof address !== "undefined" && address !== null) {
                    if (typeof (address) !== "string") {
                        address = `${address.postalCode} ${address.city}`;
                    }
                    this.setLocation(address);
                }

                if (this.props.markers) {
                    Object.values(this.props.markers).forEach(marker => this.addMarker(marker));
                }
            });
        }
    }

    componentWillUpdate(nextProps) {
        if (nextProps.address !== this.props.address) {
            this.setLocation(nextProps.address);
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.markers !== prevProps.markers) {
            this.store.clear();
            if (typeof this.cluster !== "undefined") {
                this.cluster.clearMarkers();
            }
            Object.values(this.props.markers).forEach(marker => this.addMarker(marker));
        }
    }

    onLocationChange(address, location) {
        if (typeof this.props.onLocationChange !== "undefined") {
            this.props.onLocationChange(address, location);
        }
    }

    setLocation(address) {
        if (typeof this.geocoder === "undefined") {
            return;
        }
        this.geocoder.geocode({ address }, (results, status) => {
            if (status === this.google.maps.GeocoderStatus.OK) {
                this.map.setCenter(results[0].geometry.location);
            }
        });
    }

    addMarker(data, active) {
        if (typeof this.geocoder === "undefined") {
            return;
        }
        if (typeof data.coordinates === "undefined" && typeof data.address !== "undefined") {
            this.geocoder.geocode({ address: data.address }, (results, status) => {
                if (status === this.google.maps.GeocoderStatus.OK) {
                    this.addMarker(extend(data, { coordinates: {
                        latitude: results[0].geometry.location.lat(),
                        longitude: results[0].geometry.location.lng(),
                    } }), active);
                }
            });

            return;

        }

        const latLng = new this.google.maps.LatLng(data.coordinates.latitude, data.coordinates.longitude);

        const marker = new this.google.maps.Marker({
            position: latLng,
            map: this.map,
            title: data.name,
            icon: data.active ? this.activeIcon : this.defaultIcon,
            id: data.id,
        });

        if (typeof this.cluster !== "undefined") {
            this.cluster.addMarker(marker);
        }
        this.google.maps.event.addListener(marker, "click", () => {
            this.selectMarker(marker, data);
        });
        this.store.add(marker);
    }

    selectMarker(selectedMarker, data) {
        Object.values(this.store.markers).forEach(marker => marker.setIcon(this.defaultIcon));
        selectedMarker.setIcon(this.activeIcon);
        if (typeof this.props.onChange !== "undefined") {
            this.props.onChange(data);
        }
    }

    render() {
        const GoogleMapView = ComponentRegistry.get(this.props.view);

        return (
          <GoogleMapView {...this.props}>
            <div className="map" style={{ width: "100%", height: "400px" }} ref={(node) => { this.mapNode = node; }} />
          </GoogleMapView>
        );
    }
}

GoogleMapController.propTypes = {
    view: PropTypes.string.isRequired,
    onLocationChange: PropTypes.func,
    onChange: PropTypes.func,
    defaultIcon: PropTypes.shape({
        url: PropTypes.string,
        height: PropTypes.number,
        width: PropTypes.number,
    }),
    activeIcon: PropTypes.shape({
        url: PropTypes.string,
        height: PropTypes.number,
        width: PropTypes.number,
    }),
    clusterOptions: PropTypes.shape({
        imagePath: PropTypes.string,
    }),
    options: PropTypes.shape({
        zoom: PropTypes.number,
        center: PropTypes.shape({
            latitude: PropTypes.number,
            longitude: PropTypes.number,
        }),
    }),
    markers: PropTypes.arrayOf(PropTypes.shape({})),
    address: PropTypes.string,
    libraries: PropTypes.arrayOf(PropTypes.string),
    apiKey: PropTypes.string.isRequired,
    language: PropTypes.string,
    region: PropTypes.string,
};

GoogleMapController.defaultProps = {
    view: "MapViewsGoogleMapView",
    defaultIcon: {
        url: "/assets/default/img/marker.png",
        height: 32,
        width: 42,
    },
    activeIcon: {
        url: "/assets/default/img/marker-selected.png",
        height: 32,
        width: 42,
    },
    clusterOptions: {
        imagePath: "/assets/default/img/cluster/",
    },
    options: {
        zoom: 13,
        center: {
            latitude: 0,
            longitude: 0,
        },
    },
    language: "en",
    libraries: [],
    apiKey: "AIzaSyAVk4jl4KI45oHtGbiG2snSOkcrPWIM82c",
    store: null,
};



// WEBPACK FOOTER //
// ./src-js/Fratello/Map/Controllers/GoogleMapController.jsx